import { useEffect, useCallback, useMemo } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Info, Package, Settings2 } from 'lucide-react';
import { useWizardStore, useWizardActions, useWizardUserMigrationEnabled } from '@/stores';
import { useModules } from '@/features/migrations/hooks/useModules';
import { parseMetaFields } from '@/lib/utils/metaFields';
import { cn } from '@/lib/utils';
import type { MigrationProperty } from '@/types';

export function ModuleSelectionStep() {
  const selectedModules = useWizardStore((s) => s.selectedModules);
  const matchedModules = useWizardStore((s) => s.matchedModules);
  const modulePropertyOverrides = useWizardStore((s) => s.modulePropertyOverrides);
  const userMigrationEnabled = useWizardUserMigrationEnabled();
  const {
    setSelectedModules,
    setModulePropertyOverrides,
    setModulePropertyValue,
    setIsValid,
    setUserMigrationEnabled,
  } = useWizardActions();

  const { data: moduleConfigs } = useModules();

  // Determine which selected modules have configurable properties
  const modulesWithProperties = useMemo(() => {
    if (!moduleConfigs) return [];
    return selectedModules
      .map((selected) => {
        const config = moduleConfigs.find((c) => c.moduleKey === selected.moduleKey);
        if (!config?.metaFields) return null;
        const properties = parseMetaFields(config.metaFields);
        if (properties.length === 0) return null;
        return {
          moduleKey: selected.moduleKey,
          displayName: config.displayName,
          defaultProperties: properties,
        };
      })
      .filter(
        (m): m is { moduleKey: string; displayName: string; defaultProperties: MigrationProperty[] } =>
          m !== null,
      );
  }, [selectedModules, moduleConfigs]);

  // Initialize property overrides from admin defaults for modules not yet initialized
  useEffect(() => {
    for (const mod of modulesWithProperties) {
      if (!modulePropertyOverrides[mod.moduleKey]) {
        setModulePropertyOverrides(
          mod.moduleKey,
          mod.defaultProperties.map((p) => ({ ...p })),
        );
      }
    }
  }, [modulesWithProperties, modulePropertyOverrides, setModulePropertyOverrides]);

  // Validate that at least one module is selected
  const validateSelection = useCallback(() => {
    setIsValid(selectedModules.length > 0);
  }, [selectedModules.length, setIsValid]);

  // Validate on mount and when selection changes
  useEffect(() => {
    validateSelection();
  }, [validateSelection]);

  const handleToggleModule = useCallback(
    (moduleKey: string) => {
      const matchedModule = matchedModules.find((m) => m.moduleKey === moduleKey);
      if (!matchedModule) return;

      const exists = selectedModules.some((m) => m.moduleKey === moduleKey);

      const updated = exists
        ? selectedModules.filter((m) => m.moduleKey !== moduleKey)
        : [...selectedModules, {
            moduleKey,
            sourceId: matchedModule.labelId,
            amplifyModuleId: matchedModule.amplifyModuleId ?? 0,
            bridgeProductId: matchedModule.bridgeProductId,
          }];

      setSelectedModules(updated);
    },
    [matchedModules, selectedModules, setSelectedModules]
  );

  const handleSelectAll = useCallback(() => {
    const allSelected = matchedModules.map((m) => ({
      moduleKey: m.moduleKey,
      sourceId: m.labelId,
      amplifyModuleId: m.amplifyModuleId ?? 0,
      bridgeProductId: m.bridgeProductId,
    }));
    setSelectedModules(allSelected);
  }, [matchedModules, setSelectedModules]);

  const handleDeselectAll = useCallback(() => {
    setSelectedModules([]);
  }, [setSelectedModules]);

  // No matched modules from Bridge lookup
  if (matchedModules.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>No Modules Available</AlertTitle>
        <AlertDescription>
          No modules were found during Bridge lookup. Please go back and verify the Bridge entity UUID.
        </AlertDescription>
      </Alert>
    );
  }

  const allSelected = selectedModules.length === matchedModules.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Select the modules you want to migrate. Source IDs are provided by Bridge.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-primary hover:underline"
            disabled={allSelected}
          >
            Select All
          </button>
          <span className="text-muted-foreground">|</span>
          <button
            type="button"
            onClick={handleDeselectAll}
            className="text-sm text-primary hover:underline"
            disabled={selectedModules.length === 0}
          >
            Deselect All
          </button>
        </div>
      </div>

      {selectedModules.length === 0 && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Modules Selected</AlertTitle>
          <AlertDescription>
            Please select at least one module to migrate.
          </AlertDescription>
        </Alert>
      )}

      {/* Module Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {matchedModules.map((module) => {
          const isSelected = selectedModules.some((m) => m.moduleKey === module.moduleKey);

          return (
            <Card
              key={module.moduleKey}
              className={cn(
                'cursor-pointer transition-all hover:border-primary/50',
                isSelected && 'border-primary ring-2 ring-primary/20'
              )}
              onClick={() => handleToggleModule(module.moduleKey)}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleModule(module.moduleKey);
                }
              }}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      )}
                    >
                      <Package className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{module.displayName}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {module.productLabel}
                      </p>
                    </div>
                  </div>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleModule(module.moduleKey)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${module.displayName}`}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Source ID:</span>
                  <Badge variant="secondary" className="font-mono">
                    {module.labelId}
                  </Badge>
                </div>
                {isSelected && module.userMigrationAvailable && (
                  <div
                    className="flex items-center gap-2 mt-3 pt-3 border-t"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Switch
                      id={`user-migration-${module.moduleKey}`}
                      checked={userMigrationEnabled[module.moduleKey] ?? true}
                      onCheckedChange={(checked) =>
                        setUserMigrationEnabled(module.moduleKey, checked)
                      }
                    />
                    <label
                      htmlFor={`user-migration-${module.moduleKey}`}
                      className="text-sm text-muted-foreground cursor-pointer"
                    >
                      Include user migration
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Module Properties */}
      {modulesWithProperties.length > 0 && (
        <div className="border-t pt-6 space-y-6">
          <div>
            <h3 className="text-lg font-medium">Module Properties</h3>
            <p className="text-sm text-muted-foreground">
              Review and customize migration properties for each module. Default values are provided by
              the admin configuration.
            </p>
          </div>

          {modulesWithProperties.map((mod) => {
            const overrides = modulePropertyOverrides[mod.moduleKey] ?? mod.defaultProperties;

            return (
              <Card key={mod.moduleKey}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Settings2 className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">{mod.displayName}</CardTitle>
                    <Badge variant="secondary">
                      {overrides.length} {overrides.length === 1 ? 'property' : 'properties'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {overrides.map((prop) => (
                      <div key={prop.key} className="grid grid-cols-3 items-center gap-4">
                        <Label htmlFor={`${mod.moduleKey}-${prop.key}`} className="text-sm">
                          {prop.label}
                        </Label>
                        <div className="col-span-2">
                          {prop.type === 'boolean' ? (
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`${mod.moduleKey}-${prop.key}`}
                                checked={prop.value === true}
                                onCheckedChange={(checked) =>
                                  setModulePropertyValue(mod.moduleKey, prop.key, checked)
                                }
                              />
                              <span className="text-sm text-muted-foreground">
                                {prop.value === true ? 'true' : 'false'}
                              </span>
                            </div>
                          ) : prop.type === 'integer' ? (
                            <Input
                              id={`${mod.moduleKey}-${prop.key}`}
                              type="number"
                              value={typeof prop.value === 'number' ? prop.value : ''}
                              onChange={(e) =>
                                setModulePropertyValue(
                                  mod.moduleKey,
                                  prop.key,
                                  e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                                )
                              }
                            />
                          ) : (
                            <Input
                              id={`${mod.moduleKey}-${prop.key}`}
                              value={typeof prop.value === 'string' ? prop.value : String(prop.value)}
                              onChange={(e) =>
                                setModulePropertyValue(mod.moduleKey, prop.key, e.target.value)
                              }
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
